import { Flex, Popover, PopoverArrow, PopoverContent, PopoverTrigger, Tooltip, useBoolean } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { HELP_LINK } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useZendesk } from 'libs/third-party/Zendesk/zendeskProvider';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { createHelpLinks } from './Helplink.config';
import { LinkButton } from './LinkButton';
import { LinkButtonKey, handleLinkButtonMixpanelKey } from './LinkButton.helpers';

const StyledHelp = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1.5px solid var(--vg-colors-black);
  border-radius: 50%;
  font-size: var(--vg-fontSizes-sm);
  font-weight: 400;
  color: var(--vg-colors-black);
  transition: all 0.12s;

  &:hover,
  &:focus {
    boxshadow: var(--vg-shadows-outline);
  }

  &:hover {
    background-color: var(--vg-colors-hover-gray);
  }
`;

export const HelpLink = (): ReactElement => {
  const { t } = useTranslation('common_navbar');
  const toolTipLabel = t('navbar.help-link-tooltip');
  const { show } = useZendesk();

  const [isTooltipOpen, setIsTooltipOpen] = useBoolean(false);
  const [isPopoverOpen, setIsPopoverOpen] = useBoolean(false);

  const handleOnClickHelpLink = (id: LinkButtonKey) => {
    Mixpanel.track(HELP_LINK.eventName, {
      from: handleLinkButtonMixpanelKey(id),
    });
    if (id === 'chat-with-us' && show) show();
  };

  return (
    <Tooltip hasArrow isOpen={isTooltipOpen && !isPopoverOpen} placement='bottom' label={toolTipLabel} bg='black'>
      <div
        onMouseEnter={setIsTooltipOpen.on}
        onFocus={setIsTooltipOpen.on}
        onBlur={setIsTooltipOpen.off}
        onMouseLeave={setIsTooltipOpen.off}
      >
        <Popover
          isOpen={isPopoverOpen}
          placement='bottom-end'
          onClose={setIsPopoverOpen.off}
          closeOnBlur={true}
          closeOnEsc={true}
          isLazy
        >
          <PopoverTrigger>
            <StyledHelp onClick={setIsPopoverOpen.toggle} aria-label='Help' data-testid='helplink-button'>
              ?
            </StyledHelp>
          </PopoverTrigger>
          <HelpLinkContent onClickHandler={handleOnClickHelpLink} />
        </Popover>
      </div>
    </Tooltip>
  );
};

const HelpLinkContent = ({ onClickHandler }: { onClickHandler: (id: LinkButtonKey) => void }) => {
  const { t } = useTranslation('common_navbar');
  const HELP_LINKS = createHelpLinks(t);

  return (
    <PopoverContent width='200px' boxShadow='md' padding='0'>
      <PopoverArrow />
      <Flex direction='column' data-testid='helplink-menu'>
        {HELP_LINKS.map((link) => {
          const { key, url, title } = link;
          return (
            <LinkButton key={key} id={key} href={url} target='_blank' onClick={onClickHandler}>
              {title}
            </LinkButton>
          );
        })}
      </Flex>
    </PopoverContent>
  );
};
